import { createSlice } from "@reduxjs/toolkit"
import client from "../../lib/client"
import { dynamicUrl, userRolesUrl, userRoleUrl } from "../../lib/endpoints"
import { notification } from "antd"
import { clientUserRootPath } from "../../lib/roles"

export const userRolesSlice = createSlice({
    name: "userRoles",
    initialState: {
        loading: false,
        userRoles: [],
        userRole: {},
        userRolesForUser: null,
        userRolesLoading: true,
        clientOnlyUser: false,
        isMucAdmin: false,
        isMucFinanceUser: false,
        roles: []
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload
        },
        setUserRoles: (state, action) => {
            state.userRoles = action.payload
        },
        setUserRole: (state, action) => {
            state.userRole = action.payload
        },
        removeUserRole: (state, action) => {
            state.userRoles = state.userRoles.filter((ur) => {
                return ur.id !== action.payload
            })
        },
        setUserRolesForUser: (state, action) => {
            state.userRolesForUser = action.payload
        },
        setUserRolesLoading: (state, action) => {
            state.userRolesLoading = action.payload
        },
        setClientOnlyUser: (state, action) => {
            state.clientOnlyUser = action.payload
        },
        setRoles: (state, action) => {
            state.roles = action.payload
        },
        setIsMucAdmin: (state, action) => {
            state.isMucAdmin = action.payload
        },
        setIsMucFinanceUser: (state, action) => {
            state.isMucFinanceUser = action.payload
        }
    },
})

export const { setUserRoles, setLoading, setUserRole, removeUserRole, setUserRolesForUser, setUserRolesLoading, setClientOnlyUser, setIsMucAdmin, setRoles, setIsMucFinanceUser } =
    userRolesSlice.actions

export function fetchUserRoles(userId) {
    return async (dispatch) => {
        dispatch(setLoading(true))

        client
            .get(userRolesUrl(userId))
            .then(({ data }) => {
                dispatch(
                    setUserRoles(data)

                    // setUserRoles(
                    //     data.map((i) => {
                    //         return {
                    //             ...i.user_role,
                    //             user_role_clients_attributes: i.user_role_clients,
                    //             user_role_divisions_attributes: i.user_role_divisions,
                    //             user_role_facilities_attributes: i.user_role_facilities,
                    //             user_role_billable_accounts_attributes: i.user_role_billable_accounts,
                    //             user_role_supply_accounts_attributes: i.user_role_supply_accounts,
                    //         }
                    //     })
                    // )
                )
            })
            .catch((error) => {
                console.warn(error)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function fetchUserRole(userId, roleId) {
    return async (dispatch) => {
        dispatch(setLoading(true))

        client
            .get(userRoleUrl(userId, roleId))
            .then(({ data }) => {
                // console.log(data)
                dispatch(setUserRole(data))
            })
            .catch((e) => {
                notification.error({
                    message: "Request error",
                    description: e?.response?.statusText,
                    duration: 0.8,
                })
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function fetchUserRoleForUser(clientId, redirect) {
    return async (dispatch) => {
        client
            .get(dynamicUrl(`user_roles/for_user?client_id=${clientId || ""}`))
            .then(({ data }) => {
                // console.log(data)
                dispatch(setUserRolesForUser(data?.permissions))

                // check for redirect client id -- if there is one use the history.push callback that
                // was given as an arg to redirect to that url...
                if (data?.redirect_client_id && typeof redirect === "function") {
                    dispatch(setClientOnlyUser(true))
                    redirect(clientUserRootPath(data.redirect_client_id, data?.permissions))
                }

                if (data?.redirect_to_home && typeof redirect === "function") {
                    redirect('/')
                }

                if (data?.client_only_user) {
                    dispatch(setClientOnlyUser(true))
                }

                dispatch(setRoles(data.roles))
                if(data?.roles?.indexOf('muc_admin') >= 0){
                    dispatch(setIsMucAdmin(true))
                }
                if(data?.roles?.indexOf('muc_finance_user') >= 0){
                    dispatch(setIsMucFinanceUser(true))
                }
                dispatch(setUserRolesLoading(false))
            })
            .catch((e) => {
                notification.error({
                    message: "Request error",
                    description: e?.response?.statusText,
                    duration: 0.8,
                })
            })
    }
}

export function createUserRole(userId, values) {
    return async () => {
        try {
            const response = await client.post(userRolesUrl(userId), {
                user_role: values,
            })

            return response?.data
        } catch (e) {
            notification.error({
                message: "Request error",
                description: e?.response?.statusText,
                duration: 0.8,
            })
        }
    }
}

export function updateUserRole(userId, roleId, values) {
    return async () => {
        try {
            if(values.client_ids === undefined){
                values.client_ids = []
            }
            const response = await client.patch(userRoleUrl(userId, roleId), {
                user_role: values,
            })

            return response?.data
        } catch (e) {
            notification.error({
                message: "Request error",
                description: e?.response?.statusText,
                duration: 0.8,
            })
        }
    }
}

export function deleteUserRole(userId, roleId) {
    return async (dispatch) => {
        client
            .delete(userRoleUrl(userId, roleId))
            .then(() => {
                dispatch(removeUserRole(roleId))

                notification.success({
                    message: "Success",
                    description: "User role deleted",
                    duration: 0.8,
                })
            })
            .catch((e) => {
                console.error(e)
                notification.error({
                    message: "Request error",
                    description: e?.response?.statusText,
                    duration: 0.8,
                })
            })
    }
}

export const selectUserRoles = (state) => state.userRoles.userRoles
export const selectUserRolesForUser = (state) => state.userRoles.userRolesForUser
export const selectUserRole = (state) => state.userRoles.userRole
export const selectLoading = (state) => state.userRoles.loading
export const selectUserRolesLoading = (state) => state.userRoles.userRolesLoading
export const selectClientOnlyUser = (state) => state.userRoles.clientOnlyUser
export const selectIsMucAdmin = (state) => state.userRoles.isMucAdmin
export const selectIsMucFinanceUser = (state) => state.userRoles.isMucFinanceUser
export const selectRoles = (state) => state.userRoles.roles

export default userRolesSlice.reducer
